import React from "react";
import {
  Card,
  CardContent,
  Typography,
  Stack,
  CircularProgress,
  Alert,
  Box,
  Grid,
  Divider,
} from "@mui/material";

const money = (n) =>
  Number.isFinite(Number(n)) ? `$${Number(n).toFixed(2)}` : null;

export default function ReviewPricePreview({ busy, error, totals }) {
  const itemsTotal = money(totals?.itemsTotal);
  const depositsTotal = money(totals?.depositsTotal ?? totals?.depositTotal);
  const deliveryFee = money(totals?.deliveryFee);
  const setupFee = money(totals?.setupFee);
  const discountsTotal = money(totals?.discountsTotal ?? totals?.discountTotal);
  const taxTotal = money(totals?.taxTotal);
  const grandTotal = money(
    totals?.withDepositsTotal ?? totals?.grandTotal ?? totals?.totalWithDeposits
  );

  const hasAnyRow =
    itemsTotal ||
    depositsTotal ||
    deliveryFee ||
    setupFee ||
    discountsTotal ||
    taxTotal ||
    grandTotal;

  return (
    <Card variant="outlined" sx={{ borderRadius: 3, mb: 2 }}>
      <CardContent sx={{ p: 2.25 }}>
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between">
          <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
            Price preview
          </Typography>
          {busy && <CircularProgress size={18} />}
        </Stack>

        {/* Prefer showing totals if available, even when error exists */}
        {hasAnyRow ? (
          <Box sx={{ mt: 1 }}>
            <Grid container rowGap={0.5}>
              {itemsTotal && (
                <>
                  <Grid item xs={7}>
                    <Typography variant="body2" color="text.secondary">
                      Items total
                    </Typography>
                  </Grid>
                  <Grid item xs={5} sx={{ textAlign: "right" }}>
                    <Typography variant="body2">{itemsTotal}</Typography>
                  </Grid>
                </>
              )}

              {discountsTotal && (
                <>
                  <Grid item xs={7}>
                    <Typography variant="body2" color="text.secondary">
                      Discounts
                    </Typography>
                  </Grid>
                  <Grid item xs={5} sx={{ textAlign: "right" }}>
                    <Typography variant="body2">−{discountsTotal}</Typography>
                  </Grid>
                </>
              )}

              {deliveryFee && (
                <>
                  <Grid item xs={7}>
                    <Typography variant="body2" color="text.secondary">
                      Delivery
                    </Typography>
                  </Grid>
                  <Grid item xs={5} sx={{ textAlign: "right" }}>
                    <Typography variant="body2">{deliveryFee}</Typography>
                  </Grid>
                </>
              )}

              {setupFee && (
                <>
                  <Grid item xs={7}>
                    <Typography variant="body2" color="text.secondary">
                      Setup
                    </Typography>
                  </Grid>
                  <Grid item xs={5} sx={{ textAlign: "right" }}>
                    <Typography variant="body2">{setupFee}</Typography>
                  </Grid>
                </>
              )}

              {taxTotal && (
                <>
                  <Grid item xs={7}>
                    <Typography variant="body2" color="text.secondary">
                      Estimated tax
                    </Typography>
                  </Grid>
                  <Grid item xs={5} sx={{ textAlign: "right" }}>
                    <Typography variant="body2">{taxTotal}</Typography>
                  </Grid>
                </>
              )}

              {depositsTotal && (
                <>
                  <Divider sx={{ my: 1 }} />
                  <Grid item xs={7}>
                    <Typography variant="body2" color="text.secondary">
                      Refundable deposits
                    </Typography>
                  </Grid>
                  <Grid item xs={5} sx={{ textAlign: "right" }}>
                    <Typography variant="body2">{depositsTotal}</Typography>
                  </Grid>
                </>
              )}

              {grandTotal && (
                <>
                  <Divider sx={{ my: 1.25 }} />
                  <Grid item xs={7}>
                    <Typography variant="body1" sx={{ fontWeight: 700 }}>
                      Est. due{depositsTotal ? " incl. deposits" : ""}
                    </Typography>
                  </Grid>
                  <Grid item xs={5} sx={{ textAlign: "right" }}>
                    <Typography variant="body1" sx={{ fontWeight: 700 }}>
                      {grandTotal}
                    </Typography>
                  </Grid>
                </>
              )}
            </Grid>

            {/* If there was an error but we still computed something, show it softly */}
            {error && (
              <Alert severity="warning" sx={{ mt: 1 }}>
                {error}
              </Alert>
            )}
          </Box>
        ) : error ? (
          <Alert severity="error" sx={{ mt: 1 }}>
            {error}
          </Alert>
        ) : (
          !busy && (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              Complete selections to see a price preview.
            </Typography>
          )
        )}
      </CardContent>
    </Card>
  );
}
